import JSZip from 'jszip';

/**
 * Downloads the Chrome extension as a ZIP file
 * @returns {Promise<void>}
 */
export async function downloadChromeExtension() {
    const zip = new JSZip();

    // Define all files to include in the extension
    const extensionFiles = [
        // Root files
        { path: 'manifest.json', url: '/chrome-extension/manifest.json' },
        { path: 'INSTALLATION.md', url: '/chrome-extension/INSTALLATION.md' },

        // Background files
        {
            path: 'background/background.js',
            url: '/chrome-extension/background/background.js',
        },

        // Config files
        { path: 'config/config.js', url: '/chrome-extension/config/config.js' },

        // Icon files
        {
            path: 'icons/favicon.svg',
            url: '/chrome-extension/icons/favicon.svg',
        },

        // Popup files
        { path: 'popup/popup.html', url: '/chrome-extension/popup/popup.html' },
        { path: 'popup/popup.css', url: '/chrome-extension/popup/popup.css' },
        { path: 'popup/popup.js', url: '/chrome-extension/popup/popup.js' },
    ];

    try {
        // Fetch all files and add them to the ZIP
        const filePromises = extensionFiles.map(async file => {
            try {
                const response = await fetch(file.url);
                if (!response.ok) {
                    throw new Error(
                        `Failed to fetch ${file.path}: ${response.statusText}`
                    );
                }
                const content = await response.text();
                zip.file(file.path, content);
            } catch (error) {
                console.error(`Error fetching ${file.path}:`, error);
                throw error;
            }
        });

        await Promise.all(filePromises);

        // Generate the ZIP file
        const blob = await zip.generateAsync({ type: 'blob' });

        // Create filename with timestamp
        const now = new Date();
        const timestamp = now
            .toISOString()
            .replace(/T/, '-')
            .replace(/:/g, '')
            .replace(/\..+/, '')
            .slice(0, 15); // Format: YYYY-MM-DD-HHMMSS
        const filename = `bookmarks-chrome-extension-${timestamp}.zip`;

        // Trigger download
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        return { success: true, filename };
    } catch (error) {
        console.error('Error creating extension ZIP:', error);
        throw new Error(
            'Failed to download Chrome extension. Please try again.',
            { cause: error }
        );
    }
}

/**
 * Downloads the Edge extension as a ZIP file
 * @returns {Promise<void>}
 */
export async function downloadEdgeExtension() {
    const zip = new JSZip();

    // Define all files to include in the extension
    const extensionFiles = [
        // Root files
        { path: 'manifest.json', url: '/edge-extension/manifest.json' },
        { path: 'INSTALLATION.md', url: '/edge-extension/INSTALLATION.md' },

        // Background files
        {
            path: 'background/background.js',
            url: '/edge-extension/background/background.js',
        },

        // Config files
        { path: 'config/config.js', url: '/edge-extension/config/config.js' },

        // Icon files
        { path: 'icons/favicon.svg', url: '/edge-extension/icons/favicon.svg' },

        // Popup files
        { path: 'popup/popup.html', url: '/edge-extension/popup/popup.html' },
        { path: 'popup/popup.css', url: '/edge-extension/popup/popup.css' },
        { path: 'popup/popup.js', url: '/edge-extension/popup/popup.js' },
    ];

    try {
        // Fetch all files and add them to the ZIP
        const filePromises = extensionFiles.map(async file => {
            try {
                const response = await fetch(file.url);
                if (!response.ok) {
                    throw new Error(
                        `Failed to fetch ${file.path}: ${response.statusText}`
                    );
                }
                const content = await response.text();
                zip.file(file.path, content);
            } catch (error) {
                console.error(`Error fetching ${file.path}:`, error);
                throw error;
            }
        });

        await Promise.all(filePromises);

        // Generate the ZIP file
        const blob = await zip.generateAsync({ type: 'blob' });

        // Create filename with timestamp
        const now = new Date();
        const timestamp = now
            .toISOString()
            .replace(/T/, '-')
            .replace(/:/g, '')
            .replace(/\..+/, '')
            .slice(0, 15); // Format: YYYY-MM-DD-HHMMSS
        const filename = `bookmarks-edge-extension-${timestamp}.zip`;

        // Trigger download
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        return { success: true, filename };
    } catch (error) {
        console.error('Error creating extension ZIP:', error);
        throw new Error('Failed to download Edge extension. Please try again.', { cause: error });
    }
}
